import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiX, FiFileText, FiUser, FiPlus, FiTrash2, FiPackage } = FiIcons;

const ProposalModal = ({ isOpen, onClose, proposal }) => {
  const { addProposal, updateProposal, contacts } = useData();
  const [formData, setFormData] = useState({
    title: '',
    contactId: '',
    description: '',
    designSettings: {
      template: 'modern',
      accentColor: '#3B82F6',
      headerStyle: 'clean',
      showCompanyLogo: true,
      customCSS: ''
    },
    options: [
      { id: 1, name: 'Option A', description: '', price: '', features: [''], addOns: [] },
      { id: 2, name: 'Option B', description: '', price: '', features: [''], addOns: [] }
    ],
    availableAddOns: [
      { id: 1, name: 'Priority Support', description: '24/7 support', price: 299 },
      { id: 2, name: 'Maintenance', description: 'Monthly maintenance', price: 199 }
    ]
  });

  useEffect(() => {
    if (proposal) {
      setFormData({
        ...proposal,
        contactId: proposal.contactId || '',
        designSettings: proposal.designSettings || formData.designSettings,
        availableAddOns: proposal.availableAddOns || formData.availableAddOns
      });
    } else if (isOpen) {
      setFormData({
        title: '',
        contactId: '',
        description: '',
        designSettings: {
          template: 'modern',
          accentColor: '#3B82F6',
          headerStyle: 'clean',
          showCompanyLogo: true,
          customCSS: ''
        },
        options: [
          { id: 1, name: 'Option A', description: '', price: '', features: [''], addOns: [] },
          { id: 2, name: 'Option B', description: '', price: '', features: [''], addOns: [] }
        ],
        availableAddOns: [
          { id: 1, name: 'Priority Support', description: '24/7 support', price: 299 },
          { id: 2, name: 'Maintenance', description: 'Monthly maintenance', price: 199 }
        ]
      });
    }
  }, [proposal, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totalValue = Math.max(...formData.options.map(opt => parseFloat(opt.price) || 0));
    const data = { ...formData, totalValue };
    
    if (proposal) await updateProposal(proposal.id, data);
    else await addProposal(data);
    onClose();
  };

  const addOption = () => {
    const newOption = { id: Date.now(), name: `Option ${String.fromCharCode(65 + formData.options.length)}`, description: '', price: '', features: [''], addOns: [] };
    setFormData(prev => ({ ...prev, options: [...prev.options, newOption] }));
  };

  const updateOption = (optionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map(opt => opt.id === optionId ? { ...opt, [field]: value } : opt)
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl overflow-hidden">
          <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
              <SafeIcon icon={FiFileText} className="text-blue-600" />
              {proposal ? 'Edit Proposal' : 'New Interactive Proposal'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="text-slate-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Proposal Title</label>
                <input required className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Link to Contact</label>
                <select required className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold appearance-none" value={formData.contactId} onChange={e => setFormData({ ...formData, contactId: e.target.value })}>
                  <option value="">Select a Contact</option>
                  {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Package Options</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.options.map(opt => (
                  <div key={opt.id} className="p-6 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                    <input className="w-full bg-transparent font-black text-slate-900 outline-none" value={opt.name} onChange={e => updateOption(opt.id, 'name', e.target.value)} />
                    <textarea className="w-full bg-white p-3 rounded-xl border border-slate-100 text-sm" placeholder="Description" value={opt.description} onChange={e => updateOption(opt.id, 'description', e.target.value)} />
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input type="number" className="w-full pl-8 pr-4 py-2 rounded-xl border border-slate-100 font-bold" placeholder="0.00" value={opt.price} onChange={e => updateOption(opt.id, 'price', e.target.value)} />
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addOption} className="border-2 border-dashed border-slate-200 rounded-3xl flex items-center justify-center text-slate-400 hover:border-blue-600 hover:text-blue-600 transition-all p-6">
                  <SafeIcon icon={FiPlus} className="text-2xl" />
                </button>
              </div>
            </div>

            <button type="submit" className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl hover:bg-blue-700 transition-all">
              {proposal ? 'Update Proposal' : 'Create Interactive Proposal'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProposalModal;